// src/components/membership/MembershipPlans-EmojiPicker.js
// =============================================================================
// MembershipPlansEmojiPicker
// - Centralized plan -> icon mapping (SVG icons per plan)
// =============================================================================

import React from "react";

export const MEMBERSHIP_PLAN_EMOJIS = Object.freeze({
    default:             "📄",
    wildcatter:          "🧭",
    "oil-baron":         "👑",
    "big-rig":           "⚙️",
    "blackgold-revenue": "📈",
    "blackgold-jib":     "📊",
});

/**
 * Returns the fixed emoji for a plan (text fallback).
 */
export function getMembershipPlanEmoji(planId, { isWildcatterCustom = false } = {}) {
    if (isWildcatterCustom) return MEMBERSHIP_PLAN_EMOJIS.wildcatter;

    const normalizedPlanId = (planId || "").toString();
    if (normalizedPlanId && MEMBERSHIP_PLAN_EMOJIS[normalizedPlanId]) return MEMBERSHIP_PLAN_EMOJIS[normalizedPlanId];

    return MEMBERSHIP_PLAN_EMOJIS.default;
}

// Backwards-compatible aliases
export const getSelectedMembershipEmoji = getMembershipPlanEmoji;
export const getActiveMembershipEmoji = getMembershipPlanEmoji;

const svgStyle = { display: "block", width: "100%", height: "100%", overflow: "visible" };

const PlanSvgIcons = {
    // Wildcatter - Pickaxe
    wildcatter: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={svgStyle}>
            <path d="M14.5 3.5L20.5 9.5" />
            <path d="M15 9L9 15" />
            <path d="M3.5 20.5L10 14" />
            <path d="M14.5 3.5C14.5 3.5 17 2 19.5 4.5C22 7 20.5 9.5 20.5 9.5" />
            <path d="M12 12L4 20" />
        </svg>
    ),
    // BlackGold Revenue - Bar chart with trend line
    "blackgold-revenue": (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={svgStyle}>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <rect x="7" y="13" width="2.5" height="5" rx="0.5" fill="currentColor" opacity="0.5" />
            <rect x="10.75" y="10" width="2.5" height="8" rx="0.5" fill="currentColor" opacity="0.5" />
            <rect x="14.5" y="7" width="2.5" height="11" rx="0.5" fill="currentColor" opacity="0.5" />
            <path d="M7 11L10.5 8.5L14 10L18 6" />
            <path d="M15.5 6H18V8.5" />
        </svg>
    ),
    // BlackGold JIB - Clipboard with list
    "blackgold-jib": (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={svgStyle}>
            <path d="M9 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2" />
            <rect x="9" y="2" width="6" height="3" rx="1" />
            <line x1="8" y1="10" x2="16" y2="10" />
            <line x1="8" y1="13.5" x2="14" y2="13.5" />
            <line x1="8" y1="17" x2="12" y2="17" />
        </svg>
    ),
    // Oil Baron - Crown
    "oil-baron": (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={svgStyle}>
            <path d="M3 18L5 8L9 12L12 6L15 12L19 8L21 18H3Z" fill="currentColor" opacity="0.25" />
            <path d="M3 18L5 8L9 12L12 6L15 12L19 8L21 18H3Z" />
            <line x1="3" y1="18" x2="21" y2="18" strokeWidth="2" />
            <circle cx="5" cy="8" r="1" fill="currentColor" />
            <circle cx="12" cy="6" r="1" fill="currentColor" />
            <circle cx="19" cy="8" r="1" fill="currentColor" />
        </svg>
    ),
    // Big Rig - Oil Derrick
    "big-rig": (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={svgStyle}>
            <path d="M12 2L12 6" />
            <path d="M8 6H16" />
            <path d="M9 6L7 22" />
            <path d="M15 6L17 22" />
            <path d="M7 22H17" />
            <path d="M8.5 14H15.5" />
            <path d="M9.5 10H14.5" />
            <path d="M12 6V22" />
            <rect x="10" y="18" width="4" height="3" rx="0.5" fill="currentColor" opacity="0.3" />
        </svg>
    ),
    // Default - Document
    default: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={svgStyle}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
            <polyline points="14,2 14,8 20,8" />
            <line x1="8" y1="13" x2="16" y2="13" />
            <line x1="8" y1="17" x2="12" y2="17" />
        </svg>
    ),
};

function getPlanSvg(planId, isWildcatterCustom) {
    if (isWildcatterCustom) return PlanSvgIcons.wildcatter;
    const normalized = (planId || "").toString();
    return PlanSvgIcons[normalized] || PlanSvgIcons.default;
}

export function MembershipPlansEmojiPicker({
    planId,
    isWildcatterCustom = false,
    as = "div",
    className = "",
    emojiClassName = "mc-plan-emoji",
    ariaHidden = false,
}) {
    const svg = getPlanSvg(planId, isWildcatterCustom);
    const WrapperTag = as;

    return (
        <WrapperTag
            className={className}
            aria-hidden={ariaHidden}
            style={{ background: "transparent" }}
        >
            <span className={emojiClassName} aria-hidden="true" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "60%", height: "60%", color: "#C8B568" }}>{svg}</span>
        </WrapperTag>
    );
}
